import { gql } from '@apollo/client';

export const CREATE_BOOKING_INTENT = gql`
  mutation CreateBookingIntent($createBookingInput: CreateBookingInput!) {
    createBookingIntent(createBookingInput: $createBookingInput) {
      id
      guestId
      hostId
      carId
      startAt
      endAt
      pickupLocationId
      returnLocationId
      pickupLocation {
        id
        name
        address
        city
        state
        zipCode
      }
      returnLocation {
        id
        name
        address
        city
        state
        zipCode
      }
      status
      protectionPlanId
      protectionPlan
      protectionPlanEntity {
        id
        plan
        dailyPrice
        outOfPocket
        declinedProtectionOutOfPocketAmt
        declinedProtectionOutOfPocketPercent
        description
        policyDetails
        isActive
        user {
          id
          email
          displayName
        }
      }
      protectionPlanSnapshot {
        planId
        planType
        planName
        dailyPrice
        outOfPocket
        declinedProtectionOutOfPocketAmt
        declinedProtectionOutOfPocketPercent
        description
        policyDetails
        snapshotAt
      }
      receipt {
        oneWayFee
        deliveryFee
        tripFee
        tripFeeTotal
        youngsterFee
        youngsterFeeTotal
        pricePerDay
        totalDays
        carPricePerTrip
        extraMilages
        extraMilagesCost
        insurancePerDay
        insuranceByTrip
        subtotal
        paymentMethod
        taxRate
        totalPrice
        totalPriceWithFees
        postTripIncidentChargesTotal
        totalPriceWithPostTripCharges
        tax
        balance
        last4digitOfCard
        cancellationPenalty
        lastMinSurchargePercentage
        lastMinSurchargeAmount
        discountAmount
        discountPercentage
        discountTier
        securityDeposit
        couponDiscount
        couponCode
        rentalType
        allowedMileage
        monthlyTripFeeRate
        monthlyYoungsterFeeRate
      }
      createdAt
      updatedAt
    }
  }
`;

export const GET_BOOKING_DETAILS = gql`
  query GetBooking($id: String!) {
    booking(id: $id) {
      id
      shortId
      startAt
      endAt
      status
      pickupLocationId
      returnLocationId
      pickupLocation {
        id
        name
        address
        city
        state
        zipCode
      }
      returnLocation {
        id
        name
        address
        city
        state
        zipCode
      }
      protectionPlanId
      protectionPlan
      protectionPlanEntity {
        id
        plan
        dailyPrice
        outOfPocket
        declinedProtectionOutOfPocketAmt
        declinedProtectionOutOfPocketPercent
        description
        policyDetails
        isActive
        user {
          id
          email
          displayName
        }
      }
      protectionPlanSnapshot {
        planId
        planType
        planName
        dailyPrice
        outOfPocket
        declinedProtectionOutOfPocketAmt
        declinedProtectionOutOfPocketPercent
        description
        policyDetails
        snapshotAt
      }
      guest {
        id
        firstName
        lastName
        displayName
        email
        phoneNumber
      }
      guestDisplayName
      host {
        id
        firstName
        lastName
        displayName
        email
        phoneNumber
      }
      hostDisplayName
      car {
        id
        thumbnailUrl
        distanceLimit {
          dailyDistanceLimit
        }
        make
        model
        year
        vin
        licensePlate
        deposit
        location {
          id
          name
          address
          city
          state
          zipCode
          timezone
        }
      }
      receipt {
        oneWayFee
        deliveryFee
        tripFee
        tripFeeTotal
        youngsterFee
        youngsterFeeTotal
        pricePerDay
        totalDays
        carPricePerTrip
        extraMilages
        extraMilagesCost
        insurancePerDay
        insuranceByTrip
        subtotal
        paymentMethod
        taxRate
        totalPrice
        totalPriceWithFees
        postTripIncidentChargesTotal
        totalPriceWithPostTripCharges
        tax
        balance
        last4digitOfCard
        cancellationPenalty
        shortenTripPenalty
        freeCancellationServiceFee
        lastMinSurchargePercentage
        lastMinSurchargeAmount
        discountAmount
        discountPercentage
        discountTier
        securityDeposit
        couponDiscount
        couponCode
        rentalType
        allowedMileage
        monthlyTripFeeRate
        monthlyYoungsterFeeRate
      }
      paymentSummary {
        totalPaid
        totalRefunded
        netAmount
      }
      securityDepositAmount
      securityDepositStatus
      securityDepositPaymentIntentId
      securityDepositPaymentMethodId
      securityDepositCardType
      securityDepositHeldUntil
      securityDepositAutoReleaseScheduled
      securityDepositHoldRequested
      securityDepositHoldReason
      securityDepositReleaseReason
      hostFinishedCheckin
      guestFinishedCheckin
      hostFinishedCheckout
      guestFinishedCheckout
      preTripMileage
      preTripFuelLevel
      postTripMileage
      postTripFuelLevel
      computedExtraMileage
      firstPriorityHostCalcExMileage
      appliedExtraMileage
      extraMileageChargeAmount
      extraMileageChargedAt
      extraMileageInvoiceId
      actualReturnAt
      lateReturnHours
      hostCheckinAt
      guestCheckinAt
      hostCheckoutAt
      guestCheckoutAt
      additionalDrivers {
        driverId
        createdAt
      }
      additionalDriverDetails {
        id
        firstName
        lastName
        email
      }
      invoices {
        id
        amount
        status
        type
        stripePaymentIntentId
        stripeInvoiceUrl
        stripeRefundId
        evidencePhotoS3Keys
        hostDescription
        damageClaimId
        incidentLineItems {
          incidentType
          label
          amount
          description
          photoS3Keys
        }
        refundAmount
        refundStatus
        refundInitiatedAt
        refundCompletedAt
        note
        hostApprovedRefund
        hostRefundReviewedAt
        originalInvoiceId
        breakdown {
          carPricePerTrip
          insurancePerTrip
          tax
          tripFee
          oneWayFee
          youngsterFee
          taxRate
          subtotal
          totalPrice
          baseRefund
          taxRefund
          insuranceRefund
          penaltyAmount
          notes
        }
        createdAt
      }
      cancelledInitiatedAt
      cancellationReason
      isCancellationPenaltyApplied
      isCancellationPenaltyWaivedByHost
      isShortenTripPenaltyWaivedByHost
      minimumRentalDays
      maximumRentalDays
      originalCarId
      swapRequestedAt
      swapRequestedBy
      swapRequestedCarId
      swapStatus
      swapResponseDeadline
      swapApprovedAt
      swapRejectedAt
      swapCompletedAt
      swapNote
      swapStartTime
      swapHistory {
        id
        fromCarId
        toCarId
        status
        requestedAt
        requestedBy
        approvedAt
        rejectedAt
        completedAt
        swapStartTime
        note
        responseDeadline
        invoiceId
        fromCar {
          id
          make
          model
          year
        }
        toCar {
          id
          make
          model
          year
        }
        requestedByUser {
          id
          firstName
          lastName
          displayName
          email
        }
        approvedByUser {
          id
          firstName
          lastName
          displayName
          email
        }
        rejectedByUser {
          id
          firstName
          lastName
          displayName
          email
        }
      }
      activities {
        id
        activityType
        previousState
        newState
        amount
        invoiceId
        initiatedBy
        note
        metadata
        createdAt
        initiatedByUser {
          id
          firstName
          lastName
          displayName
          email
        }
      }
      createdAt
      updatedAt
      defaultPaymentMethodId
      tripStartImages {
        url
        createdAt
      }
      tripEndImages {
        url
        createdAt
      }
    }
  }
`;

export const GET_BOOKING_AS_GUEST = gql`
  query GetBookingAsGuest($id: String!) {
    getBookingAsGuest(id: $id) {
      id
      shortId
      cancellationRules {
        freeCancellationAdvanceHours
        gracePeriodHours
        processingFeePercent
      }
      guestId
      hostId
      startAt
      endAt
      status
      pickupLocationId
      returnLocationId
      pickupLocation {
        id
        name
        address
        city
        state
        zipCode
      }
      returnLocation {
        id
        name
        address
        city
        state
        zipCode
      }
      protectionPlanId
      protectionPlan
      protectionPlanEntity {
        id
        plan
        dailyPrice
        outOfPocket
        declinedProtectionOutOfPocketAmt
        declinedProtectionOutOfPocketPercent
        description
        policyDetails
        isActive
        user {
          id
          email
          displayName
        }
      }
      protectionPlanSnapshot {
        planId
        planType
        planName
        dailyPrice
        outOfPocket
        declinedProtectionOutOfPocketAmt
        declinedProtectionOutOfPocketPercent
        description
        policyDetails
        snapshotAt
      }
      guest {
        id
        firstName
        lastName
        displayName
        email
        phoneNumber
      }
      guestDisplayName
      host {
        id
        firstName
        lastName
        displayName
        email
        phoneNumber
      }
      hostDisplayName
      car {
        id
        thumbnailUrl
        distanceLimit {
          dailyDistanceLimit
        }
        make
        model
        year
        vin
        licensePlate
        deposit
        location {
          id
          name
          address
          city
          state
          zipCode
          timezone
        }
      }
      receipt {
        oneWayFee
        deliveryFee
        tripFee
        tripFeeTotal
        youngsterFee
        youngsterFeeTotal
        pricePerDay
        totalDays
        carPricePerTrip
        extraMilages
        extraMilagesCost
        insurancePerDay
        insuranceByTrip
        subtotal
        paymentMethod
        taxRate
        totalPrice
        totalPriceWithFees
        postTripIncidentChargesTotal
        totalPriceWithPostTripCharges
        tax
        balance
        last4digitOfCard
        cancellationPenalty
        shortenTripPenalty
        freeCancellationServiceFee
        lastMinSurchargePercentage
        lastMinSurchargeAmount
        discountAmount
        discountPercentage
        discountTier
        securityDeposit
        couponDiscount
        couponCode
        rentalType
        allowedMileage
        monthlyTripFeeRate
        monthlyYoungsterFeeRate
      }
      paymentSummary {
        totalPaid
        totalRefunded
        netAmount
      }
      securityDepositAmount
      securityDepositStatus
      securityDepositPaymentIntentId
      securityDepositPaymentMethodId
      securityDepositCardType
      securityDepositHeldUntil
      securityDepositAutoReleaseScheduled
      securityDepositHoldRequested
      securityDepositHoldReason
      securityDepositReleaseReason
      hostFinishedCheckin
      guestFinishedCheckin
      hostFinishedCheckout
      guestFinishedCheckout
      preTripMileage
      preTripFuelLevel
      postTripMileage
      postTripFuelLevel
      computedExtraMileage
      firstPriorityHostCalcExMileage
      appliedExtraMileage
      extraMileageChargeAmount
      extraMileageChargedAt
      extraMileageInvoiceId
      actualReturnAt
      lateReturnHours
      hostCheckinAt
      guestCheckinAt
      hostCheckoutAt
      guestCheckoutAt
      additionalDrivers {
        driverId
        createdAt
      }
      additionalDriverDetails {
        id
        firstName
        lastName
        email
      }
      invoices {
        id
        amount
        status
        type
        stripePaymentIntentId
        stripeInvoiceUrl
        stripeRefundId
        evidencePhotoS3Keys
        hostDescription
        damageClaimId
        incidentLineItems {
          incidentType
          label
          amount
          description
          photoS3Keys
        }
        refundAmount
        refundStatus
        refundInitiatedAt
        refundCompletedAt
        note
        hostApprovedRefund
        hostRefundReviewedAt
        originalInvoiceId
        breakdown {
          carPricePerTrip
          insurancePerTrip
          tax
          tripFee
          oneWayFee
          youngsterFee
          taxRate
          subtotal
          totalPrice
          baseRefund
          taxRefund
          insuranceRefund
          penaltyAmount
          notes
        }
        createdAt
      }
      cancelledInitiatedAt
      cancellationReason
      isCancellationPenaltyApplied
      isCancellationPenaltyWaivedByHost
      isShortenTripPenaltyWaivedByHost
      minimumRentalDays
      maximumRentalDays
      originalCarId
      swapRequestedAt
      swapRequestedBy
      swapRequestedCarId
      swapStatus
      swapResponseDeadline
      swapApprovedAt
      swapRejectedAt
      swapCompletedAt
      swapNote
      swapStartTime
      swapHistory {
        id
        fromCarId
        toCarId
        status
        requestedAt
        requestedBy
        approvedAt
        rejectedAt
        completedAt
        swapStartTime
        note
        responseDeadline
        invoiceId
        fromCar {
          id
          make
          model
          year
        }
        toCar {
          id
          make
          model
          year
        }
        requestedByUser {
          id
          firstName
          lastName
          displayName
          email
        }
        approvedByUser {
          id
          firstName
          lastName
          displayName
          email
        }
        rejectedByUser {
          id
          firstName
          lastName
          displayName
          email
        }
      }
      activities {
        id
        activityType
        previousState
        newState
        amount
        invoiceId
        initiatedBy
        note
        metadata
        createdAt
        initiatedByUser {
          id
          firstName
          lastName
          displayName
          email
        }
      }
      createdAt
      updatedAt
      defaultPaymentMethodId
      dlPhoto
      dlSelfiePhoto
      dlPhotoUrl
      dlSelfiePhotoUrl
      dlVerificationStatus
      dlSubmittedAt
      dlVerifiedAt
      dlRejectionReason
      tripStartImages {
        url
        createdAt
      }
      tripEndImages {
        url
        createdAt
        uploadedBy
        uploadedByName
      }
      canStartTripField
      canSubmitDlVerificationField
    }
  }
`;

export const GET_BOOKING_AS_HOST = gql`
  query GetBookingAsHost($id: String!) {
    getBookingAsHost(id: $id) {
      id
      shortId
      cancellationRules {
        freeCancellationAdvanceHours
        gracePeriodHours
        processingFeePercent
      }
      guestId
      hostId
      carId
      startAt
      endAt
      status
      pickupLocationId
      returnLocationId
      pickupLocation {
        id
        name
        address
        city
        state
        zipCode
      }
      returnLocation {
        id
        name
        address
        city
        state
        zipCode
      }
      protectionPlanId
      protectionPlan
      protectionPlanEntity {
        id
        plan
        dailyPrice
        outOfPocket
        declinedProtectionOutOfPocketAmt
        declinedProtectionOutOfPocketPercent
        description
        policyDetails
        isActive
        user {
          id
          email
          displayName
        }
      }
      protectionPlanSnapshot {
        planId
        planType
        planName
        dailyPrice
        outOfPocket
        declinedProtectionOutOfPocketAmt
        declinedProtectionOutOfPocketPercent
        description
        policyDetails
        snapshotAt
      }
      guest {
        id
        firstName
        lastName
        displayName
        email
        phoneNumber
      }
      guestDisplayName
      host {
        id
        firstName
        lastName
        displayName
        email
        phoneNumber
      }
      hostDisplayName
      car {
        id
        thumbnailUrl
        distanceLimit {
          dailyDistanceLimit
        }
        make
        model
        year
        vin
        licensePlate
        deposit
        location {
          id
          name
          address
          city
          state
          zipCode
          timezone
        }
      }
      receipt {
        oneWayFee
        deliveryFee
        tripFee
        tripFeeTotal
        youngsterFee
        youngsterFeeTotal
        pricePerDay
        totalDays
        carPricePerTrip
        extraMilages
        extraMilagesCost
        insurancePerDay
        insuranceByTrip
        subtotal
        paymentMethod
        taxRate
        totalPrice
        totalPriceWithFees
        postTripIncidentChargesTotal
        totalPriceWithPostTripCharges
        tax
        balance
        last4digitOfCard
        cancellationPenalty
        shortenTripPenalty
        freeCancellationServiceFee
        lastMinSurchargePercentage
        lastMinSurchargeAmount
        discountAmount
        discountPercentage
        discountTier
        securityDeposit
        couponDiscount
        couponCode
        rentalType
        allowedMileage
        monthlyTripFeeRate
        monthlyYoungsterFeeRate
      }
      paymentSummary {
        totalPaid
        totalRefunded
        netAmount
      }
      securityDepositAmount
      securityDepositStatus
      securityDepositPaymentIntentId
      securityDepositPaymentMethodId
      securityDepositCardType
      securityDepositHeldUntil
      securityDepositAutoReleaseScheduled
      securityDepositHoldRequested
      securityDepositHoldReason
      securityDepositReleaseReason
      hostFinishedCheckin
      guestFinishedCheckin
      hostFinishedCheckout
      guestFinishedCheckout
      preTripMileage
      preTripFuelLevel
      postTripMileage
      postTripFuelLevel
      computedExtraMileage
      firstPriorityHostCalcExMileage
      appliedExtraMileage
      extraMileageChargeAmount
      extraMileageChargedAt
      extraMileageInvoiceId
      actualReturnAt
      lateReturnHours
      hostCheckinAt
      guestCheckinAt
      hostCheckoutAt
      guestCheckoutAt
      additionalDrivers {
        driverId
        createdAt
      }
      additionalDriverDetails {
        id
        firstName
        lastName
        email
      }
      invoices {
        id
        amount
        status
        type
        stripePaymentIntentId
        stripeInvoiceUrl
        stripeRefundId
        evidencePhotoS3Keys
        hostDescription
        damageClaimId
        incidentLineItems {
          incidentType
          label
          amount
          description
          photoS3Keys
        }
        refundAmount
        refundStatus
        refundInitiatedAt
        refundCompletedAt
        note
        hostApprovedRefund
        hostRefundReviewedAt
        originalInvoiceId
        breakdown {
          carPricePerTrip
          insurancePerTrip
          tax
          tripFee
          oneWayFee
          youngsterFee
          taxRate
          subtotal
          totalPrice
          baseRefund
          taxRefund
          insuranceRefund
          penaltyAmount
          notes
        }
        createdAt
      }
      cancelledInitiatedAt
      cancellationReason
      isCancellationPenaltyApplied
      isCancellationPenaltyWaivedByHost
      isShortenTripPenaltyWaivedByHost
      minimumRentalDays
      maximumRentalDays
      originalCarId
      swapRequestedAt
      swapRequestedBy
      swapRequestedCarId
      swapStatus
      swapResponseDeadline
      swapApprovedAt
      swapRejectedAt
      swapCompletedAt
      swapNote
      swapStartTime
      swapHistory {
        id
        fromCarId
        toCarId
        status
        requestedAt
        requestedBy
        approvedAt
        rejectedAt
        completedAt
        swapStartTime
        note
        responseDeadline
        invoiceId
        fromCar {
          id
          make
          model
          year
        }
        toCar {
          id
          make
          model
          year
        }
        requestedByUser {
          id
          firstName
          lastName
          displayName
          email
        }
        approvedByUser {
          id
          firstName
          lastName
          displayName
          email
        }
        rejectedByUser {
          id
          firstName
          lastName
          displayName
          email
        }
      }
      activities {
        id
        activityType
        previousState
        newState
        amount
        invoiceId
        initiatedBy
        note
        metadata
        createdAt
        initiatedByUser {
          id
          firstName
          lastName
          displayName
          email
        }
      }
      createdAt
      updatedAt
      defaultPaymentMethodId
      dlPhoto
      dlSelfiePhoto
      dlPhotoUrl
      dlSelfiePhotoUrl
      dlVerificationStatus
      dlSubmittedAt
      dlVerifiedAt
      dlRejectionReason
      tripStartImages {
        url
        createdAt
        uploadedBy
        uploadedByName
      }
      tripEndImages {
        url
        createdAt
        uploadedBy
        uploadedByName
      }
      deletedTripStartImages {
        url
        createdAt
        uploadedBy
        uploadedByName
        deletedBy
        deletedByName
        deletedAt
      }
      deletedTripEndImages {
        url
        createdAt
        uploadedBy
        uploadedByName
        deletedBy
        deletedByName
        deletedAt
      }
      canStartTripField
      canSubmitDlVerificationField
    }
  }
`;

export const GET_BOOKING_ACTIVITIES = gql`
  query GetBookingActivities($bookingId: ID!) {
    getBookingActivities(bookingId: $bookingId) {
      id
      bookingId
      activityType
      previousState
      newState
      amount
      invoiceId
      initiatedBy
      note
      metadata
      createdAt
      initiatedByUser {
        id
        firstName
        lastName
        displayName
        email
      }
    }
  }
`;

export const REMOVE_BOOKING_PENALTIES = gql`
  mutation RemoveBookingPenalties($input: RemoveBookingPenaltiesInput!) {
    removeBookingPenalties(input: $input) {
      id
      isCancellationPenaltyWaivedByHost
      isShortenTripPenaltyWaivedByHost
      receipt {
        cancellationPenalty
        shortenTripPenalty
        freeCancellationServiceFee
      }
    }
  }
`;

export const CAN_START_TRIP = gql`
  query CanStartTrip($bookingId: String!) {
    canStartTrip(bookingId: $bookingId)
  }
`;

export const COMPLETE_HOST_CHECKIN = gql`
  mutation CompleteHostCheckin($input: CompleteHostCheckinInput!) {
    completeHostCheckin(input: $input) {
      id
      hostFinishedCheckin
      preTripMileage
      preTripFuelLevel
      hostCheckinAt
    }
  }
`;

export const COMPLETE_GUEST_CHECKIN = gql`
  mutation CompleteGuestCheckin($input: CompleteGuestCheckinInput!) {
    completeGuestCheckin(input: $input) {
      id
      guestFinishedCheckin
      guestCheckinAt
    }
  }
`;

export const COMPLETE_GUEST_CHECKOUT = gql`
  mutation CompleteGuestCheckout($input: CompleteGuestCheckoutInput!) {
    completeGuestCheckout(input: $input) {
      id
      guestFinishedCheckout
      guestCheckoutAt
      tripEndImages {
        url
        createdAt
        uploadedBy
        uploadedByName
      }
    }
  }
`;

export const COMPLETE_HOST_CHECKOUT = gql`
  mutation CompleteHostCheckout($input: CompleteHostCheckoutInput!) {
    completeHostCheckout(input: $input) {
      id
      hostFinishedCheckout
      hostCheckoutAt
      postTripMileage
      postTripFuelLevel
      computedExtraMileage
      firstPriorityHostCalcExMileage
      appliedExtraMileage
      extraMileageChargeAmount
      receipt {
        extraMilages
        extraMilagesCost
        perExtraMileCost
      }
      tripEndImages {
        url
        createdAt
        uploadedBy
        uploadedByName
      }
    }
  }
`;

export const CHARGE_EXTRA_MILEAGE = gql`
  mutation ChargeExtraMileage($bookingId: ID!) {
    chargeExtraMileage(bookingId: $bookingId) {
      success
      status
      message
      paymentLink
      invoice {
        id
        amount
        status
        type
        stripeInvoiceUrl
      }
    }
  }
`;

export const UPDATE_ACTUAL_RETURN_DATE_TIME = gql`
  mutation UpdateActualReturnDateTime($input: UpdateActualReturnDateTimeInput!) {
    updateActualReturnDateTime(input: $input) {
      id
      actualReturnAt
      lateReturnHours
    }
  }
`;

export const CHARGE_INCIDENT_FEES = gql`
  mutation ChargeIncidentFees($input: ChargeIncidentFeesInput!) {
    chargeIncidentFees(input: $input) {
      success
      status
      message
      paymentLink
      failedPaymentMethods
      invoice {
        id
        amount
        status
        type
        stripeInvoiceUrl
        evidencePhotoS3Keys
        hostDescription
        incidentLineItems {
          incidentType
          label
          amount
          description
          photoS3Keys
        }
      }
    }
  }
`;

export const CHARGE_INCIDENT_FEE = gql`
  mutation ChargeIncidentFee($input: ChargeIncidentFeeInput!) {
    chargeIncidentFee(input: $input) {
      success
      status
      message
      paymentLink
      invoice {
        id
        amount
        status
        type
        stripeInvoiceUrl
        evidencePhotoS3Keys
        hostDescription
        damageClaimId
      }
    }
  }
`;

export const GET_PRESIGNED_URL_FOR_BOOKING = gql`
  mutation GetPresignedUrlForBooking($fileType: String!, $bookingId: String!, $imageType: String!) {
    getPresignedUrlForBooking(fileType: $fileType, bookingId: $bookingId, imageType: $imageType) {
      url
      key
    }
  }
`;

export const UPLOAD_TRIP_PHOTO = gql`
  mutation UploadTripPhoto($input: UploadTripPhotoInput!) {
    uploadTripPhoto(input: $input) {
      id
      tripStartImages {
        url
        createdAt
      }
      tripEndImages {
        url
        createdAt
      }
    }
  }
`;

export const DELETE_TRIP_PHOTO = gql`
  mutation DeleteTripPhoto($input: DeleteTripPhotoInput!) {
    deleteTripPhoto(input: $input) {
      id
      tripStartImages {
        url
        createdAt
        uploadedBy
        uploadedByName
      }
      tripEndImages {
        url
        createdAt
        uploadedBy
        uploadedByName
      }
      deletedTripStartImages {
        url
        createdAt
        uploadedBy
        uploadedByName
        deletedBy
        deletedByName
        deletedAt
      }
      deletedTripEndImages {
        url
        createdAt
        uploadedBy
        uploadedByName
        deletedBy
        deletedByName
        deletedAt
      }
    }
  }
`;

export const UPDATE_BOOKING_MINIMUM_RENTAL_DAYS = gql`
  mutation UpdateBookingMinimumRentalDays($input: UpdateBookingMinimumRentalDaysInput!) {
    updateBookingMinimumRentalDays(input: $input) {
      id
      minimumRentalDays
      maximumRentalDays
    }
  }
`;

export const CANCEL_BOOKING = gql`
  mutation CancelBooking(
    $bookingId: String!
    $bookingStatus: BookingStatus!
    $balance: Float!
    $cancellationReason: CancellationReason!
    $cancelledInitiatedAt: DateTime!
    $isCancellationPenaltyApplied: Boolean!
    $invoiceId: ID!
    $cancellationPenalty: Float!
  ) {
    cancelBooking(
      bookingId: $bookingId
      bookingStatus: $bookingStatus
      balance: $balance
      cancellationReason: $cancellationReason
      cancelledInitiatedAt: $cancelledInitiatedAt
      isCancellationPenaltyApplied: $isCancellationPenaltyApplied
      invoiceId: $invoiceId
      cancellationPenalty: $cancellationPenalty
    ) {
      id
      status
      cancellationReason
      cancelledInitiatedAt
      isCancellationPenaltyApplied
      receipt {
        totalPrice
        totalPriceWithFees
        postTripIncidentChargesTotal
        totalPriceWithPostTripCharges
        balance
        cancellationPenalty
        lastMinSurchargePercentage
        lastMinSurchargeAmount
        discountAmount
        discountPercentage
        discountTier
        couponDiscount
        couponCode
        rentalType
        allowedMileage
        monthlyTripFeeRate
        monthlyYoungsterFeeRate
      }
      invoices {
        id
        amount
        status
        refundStatus
        refundAmount
        refundInitiatedAt
        refundCompletedAt
        hostApprovedRefund
        hostRefundReviewedAt
        note
      }
    }
  }
`;

export const GET_CAR_BOOKINGS = gql`
  query CarBookings($carId: String!, $status: String) {
    carBookings(carId: $carId, status: $status) {
      id
      guestId
      hostId
      carId
      startAt
      endAt
      pickupLocationId
      returnLocationId
      pickupLocation {
        id
        name
        address
        city
        state
        zipCode
      }
      returnLocation {
        id
        name
        address
        city
        state
        zipCode
      }
      status
      receipt {
        oneWayFee
        deliveryFee
        tripFee
        tripFeeTotal
        youngsterFee
        youngsterFeeTotal
        pricePerDay
        totalDays
        carPricePerTrip
        extraMilages
        extraMilagesCost
        insurancePerDay
        insuranceByTrip
        subtotal
        paymentMethod
        taxRate
        totalPrice
        tax
        balance
        last4digitOfCard
        cancellationPenalty
        shortenTripPenalty
        freeCancellationServiceFee
        lastMinSurchargePercentage
        lastMinSurchargeAmount
        discountAmount
        discountPercentage
        discountTier
        couponDiscount
        couponCode
        rentalType
        allowedMileage
        monthlyTripFeeRate
        monthlyYoungsterFeeRate
      }
      guest {
        id
        firstName
        lastName
        displayName
        email
        phoneNumber
      }
      guestDisplayName
      host {
        id
        firstName
        lastName
        displayName
        email
        phoneNumber
      }
      hostDisplayName
      car {
        id
        make
        model
        year
        thumbnailUrl
        location {
          id
          name
          address
          city
          state
          zipCode
          timezone
        }
      }
      originalCarId
      swapStatus
      swapCompletedAt
      originalCar {
        id
        make
        model
        year
        thumbnailUrl
      }
      protectionPlanId
      protectionPlan
      protectionPlanEntity {
        id
        plan
        dailyPrice
        outOfPocket
        description
        policyDetails
        isActive
        user {
          id
          email
          displayName
        }
      }
      cancelledInitiatedAt
      cancellationReason
      isCancellationPenaltyApplied
      invoices {
        id
        amount
        status
        refundStatus
        refundAmount
        hostApprovedRefund
        hostRefundReviewedAt
      }
      createdAt
      updatedAt
    }
  }
`;

export const UPDATE_BOOKING = gql`
  mutation UpdateBooking($updateBookingInput: UpdateBookingInput!) {
    updateBooking(updateBookingInput: $updateBookingInput) {
      booking {
        id
        protectionPlanId
        protectionPlan
        protectionPlanEntity {
          id
          plan
        }
      }
    }
  }
`;

export const GET_BOOKINGS_BY_GUEST_ID = gql`
  query GetMyBookings {
    myBookings {
      id
      shortId
      guestId
      hostId
      carId
      startAt
      endAt
      pickupLocationId
      returnLocationId
      pickupLocation {
        id
        name
        address
        city
        state
        zipCode
      }
      returnLocation {
        id
        name
        address
        city
        state
        zipCode
      }
      status
      receipt {
        oneWayFee
        deliveryFee
        tripFee
        tripFeeTotal
        youngsterFee
        youngsterFeeTotal
        pricePerDay
        totalDays
        carPricePerTrip
        extraMilages
        extraMilagesCost
        insurancePerDay
        insuranceByTrip
        subtotal
        paymentMethod
        taxRate
        totalPrice
        tax
        balance
        last4digitOfCard
        cancellationPenalty
        shortenTripPenalty
        freeCancellationServiceFee
        lastMinSurchargePercentage
        lastMinSurchargeAmount
        discountAmount
        discountPercentage
        discountTier
        couponDiscount
        couponCode
        rentalType
        allowedMileage
        monthlyTripFeeRate
        monthlyYoungsterFeeRate
      }
      guest {
        id
        firstName
        lastName
        displayName
        email
        phoneNumber
      }
      guestDisplayName
      host {
        id
        firstName
        lastName
        displayName
        email
        phoneNumber
      }
      hostDisplayName
      car {
        id
        make
        model
        year
        thumbnailUrl
        location {
          id
          name
          address
          city
          state
          zipCode
          timezone
        }
      }
      originalCarId
      swapStatus
      swapCompletedAt
      originalCar {
        id
        make
        model
        year
        thumbnailUrl
      }
      protectionPlanId
      protectionPlan
      protectionPlanEntity {
        id
        plan
        dailyPrice
        outOfPocket
        description
        policyDetails
        isActive
        user {
          id
          email
          displayName
        }
      }
      cancelledInitiatedAt
      cancellationReason
      isCancellationPenaltyApplied
      invoices {
        id
        amount
        status
        refundStatus
        refundAmount
        hostApprovedRefund
        hostRefundReviewedAt
      }
      createdAt
      updatedAt
    }
  }
`;

export const GET_ALL_BOOKINGS = gql`
  query bookings {
    bookings {
      id
      shortId
      guestId
      hostId
      carId
      startAt
      endAt
      pickupLocationId
      returnLocationId
      pickupLocation {
        id
        name
        address
        city
        state
        zipCode
      }
      returnLocation {
        id
        name
        address
        city
        state
        zipCode
      }
      status
      receipt {
        oneWayFee
        deliveryFee
        tripFee
        tripFeeTotal
        youngsterFee
        youngsterFeeTotal
        pricePerDay
        totalDays
        carPricePerTrip
        extraMilages
        extraMilagesCost
        insurancePerDay
        insuranceByTrip
        subtotal
        paymentMethod
        taxRate
        totalPrice
        tax
        balance
        last4digitOfCard
        cancellationPenalty
        shortenTripPenalty
        freeCancellationServiceFee
        lastMinSurchargePercentage
        lastMinSurchargeAmount
        discountAmount
        discountPercentage
        discountTier
        couponDiscount
        couponCode
        rentalType
        allowedMileage
        monthlyTripFeeRate
        monthlyYoungsterFeeRate
      }
      guest {
        id
        firstName
        lastName
        displayName
        email
        phoneNumber
      }
      guestDisplayName
      host {
        id
        firstName
        lastName
        displayName
        email
        phoneNumber
      }
      hostDisplayName
      car {
        id
        make
        model
        year
        thumbnailUrl
        location {
          id
          name
          address
          city
          state
          zipCode
          timezone
        }
      }
      originalCarId
      swapStatus
      swapCompletedAt
      originalCar {
        id
        make
        model
        year
        thumbnailUrl
      }
      protectionPlanId
      protectionPlan
      protectionPlanEntity {
        id
        plan
        dailyPrice
        outOfPocket
        description
        policyDetails
        isActive
        user {
          id
          email
          displayName
        }
      }
      cancelledInitiatedAt
      cancellationReason
      isCancellationPenaltyApplied
      invoices {
        id
        amount
        status
        refundStatus
        refundAmount
        hostApprovedRefund
        hostRefundReviewedAt
      }
      createdAt
      updatedAt
    }
  }
`;

export const GET_BOOKINGS_FOR_MY_LISTINGS = gql`
  query GetBookingsForMyListings {
    bookingsForMyListings {
      id
      shortId
      guestId
      hostId
      carId
      startAt
      endAt
      pickupLocationId
      returnLocationId
      pickupLocation {
        id
        name
        address
        city
        state
        zipCode
      }
      returnLocation {
        id
        name
        address
        city
        state
        zipCode
      }
      status
      receipt {
        oneWayFee
        deliveryFee
        tripFee
        tripFeeTotal
        youngsterFee
        youngsterFeeTotal
        pricePerDay
        totalDays
        carPricePerTrip
        extraMilages
        extraMilagesCost
        insurancePerDay
        insuranceByTrip
        subtotal
        paymentMethod
        taxRate
        totalPrice
        tax
        balance
        last4digitOfCard
        cancellationPenalty
        shortenTripPenalty
        freeCancellationServiceFee
        lastMinSurchargePercentage
        lastMinSurchargeAmount
        discountAmount
        discountPercentage
        discountTier
        couponDiscount
        couponCode
        rentalType
        allowedMileage
        monthlyTripFeeRate
        monthlyYoungsterFeeRate
      }
      guest {
        id
        firstName
        lastName
        displayName
        email
        phoneNumber
      }
      guestDisplayName
      host {
        id
        firstName
        lastName
        displayName
        email
        phoneNumber
      }
      hostDisplayName
      car {
        id
        make
        model
        year
        thumbnailUrl
        location {
          id
          name
          address
          city
          state
          zipCode
          timezone
        }
      }
      originalCarId
      swapStatus
      swapCompletedAt
      originalCar {
        id
        make
        model
        year
        thumbnailUrl
      }
      protectionPlanId
      protectionPlan
      protectionPlanEntity {
        id
        plan
        dailyPrice
        outOfPocket
        description
        policyDetails
        isActive
        user {
          id
          email
          displayName
        }
      }
      cancelledInitiatedAt
      cancellationReason
      isCancellationPenaltyApplied
      invoices {
        id
        amount
        status
        refundStatus
        refundAmount
        hostApprovedRefund
        hostRefundReviewedAt
      }
      createdAt
      updatedAt
    }
  }
`;

export const UPDATE_BOOKING_PROTECTION_PLAN = gql`
  mutation UpdateBookingProtectionPlan($input: UpdateBookingProtectionPlanInput!) {
    updateBookingProtectionPlan(input: $input) {
      id
      protectionPlanId
      protectionPlan
      protectionPlanEntity {
        id
        plan
        dailyPrice
        outOfPocket
        description
        policyDetails
        isActive
        user {
          id
          email
          displayName
        }
      }
      protectionPlanSnapshot {
        planId
        planType
        planName
        dailyPrice
        outOfPocket
        description
        policyDetails
        snapshotAt
      }
      receipt {
        pricePerDay
        totalDays
        totalPrice
        lastMinSurchargePercentage
        lastMinSurchargeAmount
        discountAmount
        discountPercentage
        discountTier
        couponDiscount
        couponCode
        rentalType
        allowedMileage
        monthlyTripFeeRate
        monthlyYoungsterFeeRate
      }
      startAt
      endAt
      status
    }
  }
`;

export const CHECK_BOOKING_AVAILABILITY = gql`
  mutation CheckCarAvailability(
    $carId: String!
    $startAt: Float!
    $endAt: Float!
    $bookingId: String
  ) {
    checkCarAvailability(carId: $carId, startAt: $startAt, endAt: $endAt, bookingId: $bookingId) {
      isAvailable
    }
  }
`;

export const GET_CANCELLED_BOOKINGS_AS_HOST = gql`
  query GetCancelledBookingsAsHost($filter: CancelledBookingsFilterInput) {
    cancelledBookingsAsHost(filter: $filter) {
      id
      guestId
      hostId
      carId
      startAt
      endAt
      pickupLocationId
      returnLocationId
      pickupLocation {
        id
        name
        address
        city
        state
        zipCode
      }
      returnLocation {
        id
        name
        address
        city
        state
        zipCode
      }
      status
      receipt {
        oneWayFee
        deliveryFee
        tripFee
        tripFeeTotal
        youngsterFee
        youngsterFeeTotal
        pricePerDay
        totalDays
        carPricePerTrip
        extraMilages
        extraMilagesCost
        insurancePerDay
        insuranceByTrip
        subtotal
        paymentMethod
        taxRate
        totalPrice
        tax
        balance
        last4digitOfCard
        cancellationPenalty
        shortenTripPenalty
        freeCancellationServiceFee
        lastMinSurchargePercentage
        lastMinSurchargeAmount
        discountAmount
        discountPercentage
        discountTier
        couponDiscount
        couponCode
        rentalType
        allowedMileage
        monthlyTripFeeRate
        monthlyYoungsterFeeRate
      }
      guest {
        id
        firstName
        lastName
        displayName
        email
        phoneNumber
      }
      guestDisplayName
      host {
        id
        firstName
        lastName
        displayName
        email
        phoneNumber
      }
      hostDisplayName
      car {
        id
        make
        model
        year
        thumbnailUrl
        location {
          id
          name
          address
          city
          state
          zipCode
          timezone
        }
      }
      originalCarId
      swapStatus
      swapCompletedAt
      originalCar {
        id
        make
        model
        year
        thumbnailUrl
      }
      protectionPlanId
      protectionPlan
      protectionPlanEntity {
        id
        plan
        dailyPrice
        outOfPocket
        description
        policyDetails
        isActive
        user {
          id
          email
          displayName
        }
      }
      cancelledInitiatedAt
      cancellationReason
      isCancellationPenaltyApplied
      invoices {
        id
        type
        amount
        status
        refundStatus
        refundAmount
        hostApprovedRefund
        hostRefundReviewedAt
        originalInvoiceId
        breakdown {
          penaltyAmount
          baseRefund
          taxRefund
          insuranceRefund
          totalRefund
          notes
        }
      }
      createdAt
      updatedAt
    }
  }
`;

export const GET_CANCELLED_BOOKINGS_AS_GUEST = gql`
  query GetCancelledBookingsAsGuest($filter: CancelledBookingsFilterInput) {
    cancelledBookingsAsGuest(filter: $filter) {
      id
      guestId
      hostId
      carId
      startAt
      endAt
      pickupLocationId
      returnLocationId
      pickupLocation {
        id
        name
        address
        city
        state
        zipCode
      }
      returnLocation {
        id
        name
        address
        city
        state
        zipCode
      }
      status
      receipt {
        oneWayFee
        deliveryFee
        tripFee
        tripFeeTotal
        youngsterFee
        youngsterFeeTotal
        pricePerDay
        totalDays
        carPricePerTrip
        extraMilages
        extraMilagesCost
        insurancePerDay
        insuranceByTrip
        subtotal
        paymentMethod
        taxRate
        totalPrice
        tax
        balance
        last4digitOfCard
        cancellationPenalty
        shortenTripPenalty
        freeCancellationServiceFee
        lastMinSurchargePercentage
        lastMinSurchargeAmount
        discountAmount
        discountPercentage
        discountTier
        couponDiscount
        couponCode
        rentalType
        allowedMileage
        monthlyTripFeeRate
        monthlyYoungsterFeeRate
      }
      guest {
        id
        firstName
        lastName
        displayName
        email
        phoneNumber
      }
      guestDisplayName
      host {
        id
        firstName
        lastName
        displayName
        email
        phoneNumber
      }
      hostDisplayName
      car {
        id
        make
        model
        year
        thumbnailUrl
        location {
          id
          name
          address
          city
          state
          zipCode
          timezone
        }
      }
      originalCarId
      swapStatus
      swapCompletedAt
      originalCar {
        id
        make
        model
        year
        thumbnailUrl
      }
      protectionPlanId
      protectionPlan
      protectionPlanEntity {
        id
        plan
        dailyPrice
        outOfPocket
        description
        policyDetails
        isActive
        user {
          id
          email
          displayName
        }
      }
      cancelledInitiatedAt
      cancellationReason
      isCancellationPenaltyApplied
      invoices {
        id
        amount
        status
        refundStatus
        refundAmount
        hostApprovedRefund
        hostRefundReviewedAt
      }
      createdAt
      updatedAt
    }
  }
`;

export const CALCULATE_TRIP_EXTENSION = gql`
  mutation CalculateTripExtension($input: CalculateTripExtensionInput!) {
    calculateTripExtension(input: $input) {
      booking {
        id
        startAt
        endAt
        status
        receipt {
          totalPrice
          totalDays
          pricePerDay
          carPricePerTrip
          tripFee
          tripFeeTotal
          youngsterFee
          youngsterFeeTotal
          oneWayFee
          extraMilages
          perExtraMileCost
          extraMilagesCost
          insurancePerDay
          insuranceByTrip
          taxRate
          tax
          subtotal
          balance
          last4digitOfCard
          shortenTripPenalty
          freeCancellationServiceFee
          lastMinSurchargePercentage
          lastMinSurchargeAmount
          discountAmount
          discountPercentage
          discountTier
        }
        protectionPlan
        protectionPlanId
        protectionPlanEntity {
          id
          plan
          dailyPrice
          description
        }
        car {
          id
          make
          model
          year
          vin
          ageLimit
          distanceLimit {
            unlimitedMileage
            dailyDistanceLimit
          }
        }
        guest {
          id
          firstName
          lastName
          email
        }
        guestDisplayName
        host {
          id
          firstName
          lastName
          email
        }
        hostDisplayName
      }
      priceDifference
      totalPrice
      shortenTripPenalty
      freeCancellationServiceFee
    }
  }
`;

export const ADD_ADDITIONAL_DRIVER = gql`
  mutation AddDriverToBooking($input: AddDriverInput!) {
    addDriverToBooking(input: $input) {
      id
      additionalDrivers {
        driverId
        createdAt
      }
    }
  }
`;

export const REQUEST_ADD_ADDITIONAL_DRIVER = gql`
  mutation RequestAddDriverToBooking($input: AddDriverInput!) {
    requestAddDriverToBooking(input: $input) {
      outcome
      message
      invitationId
      driver {
        id
        firstName
        lastName
        email
      }
      booking {
        id
        additionalDrivers {
          driverId
          createdAt
        }
        additionalDriverDetails {
          id
          firstName
          lastName
          email
        }
      }
    }
  }
`;

export const MY_ALLOWED_TO_DRIVE_TRIPS = gql`
  query MyAllowedToDriveTrips {
    myAllowedToDriveTrips {
      tripId
      carMake
      carModel
      carYear
      plateNumber
      lastFiveVin
      mainDriverName
      startAt
      endAt
      pickupCity
      returnCity
      addedAsDriverAt
      agreementAccepted
      agreementVersionId
    }
  }
`;

export const GET_BOOKING_ADDITIONAL_DRIVERS = gql`
  query GetBookingAdditionalDrivers($id: String!) {
    booking(id: $id) {
      id
      additionalDrivers {
        driverId
        createdAt
      }
      additionalDriverDetails {
        id
        firstName
        lastName
        email
      }
    }
  }
`;

export const CONFIRM_TRIP_EXTENSION = gql`
  mutation ConfirmTripExtension($input: ConfirmTripExtensionInput!) {
    confirmTripExtension(input: $input) {
      booking {
        id
        startAt
        endAt
        status
        receipt {
          totalPrice
          totalDays
          pricePerDay
          carPricePerTrip
          tripFee
          tripFeeTotal
          youngsterFee
          youngsterFeeTotal
          oneWayFee
          extraMilages
          perExtraMileCost
          extraMilagesCost
          insurancePerDay
          insuranceByTrip
          taxRate
          tax
          subtotal
          balance
          last4digitOfCard
          lastMinSurchargePercentage
          lastMinSurchargeAmount
          discountAmount
          discountPercentage
          discountTier
        }
        protectionPlan
        protectionPlanId
        protectionPlanEntity {
          id
          plan
          dailyPrice
          description
        }
        car {
          id
          make
          model
          year
          vin
          ageLimit
          distanceLimit {
            unlimitedMileage
            dailyDistanceLimit
          }
        }
        guest {
          id
          firstName
          lastName
          email
        }
        guestDisplayName
        host {
          id
          firstName
          lastName
          email
        }
        hostDisplayName
      }
      priceDifference
      totalPrice
    }
  }
`;

export const HOLD_SECURITY_DEPOSIT = gql`
  mutation HoldSecurityDeposit($bookingId: String!, $reason: String) {
    holdSecurityDeposit(bookingId: $bookingId, reason: $reason) {
      id
      securityDepositStatus
      securityDepositHoldRequested
      securityDepositAutoReleaseScheduled
    }
  }
`;

export const RELEASE_SECURITY_DEPOSIT = gql`
  mutation ReleaseSecurityDeposit($bookingId: String!, $releaseAmount: Float!, $reason: String) {
    releaseSecurityDeposit(bookingId: $bookingId, releaseAmount: $releaseAmount, reason: $reason) {
      id
      securityDepositStatus
      securityDepositAmount
      securityDepositHoldRequested
      securityDepositAutoReleaseScheduled
    }
  }
`;

export const REQUEST_CAR_SWAP = gql`
  mutation RequestCarSwap($input: CarSwapRequestInput!) {
    requestCarSwap(input: $input) {
      id
      carId
      originalCarId
      swapRequestedAt
      swapRequestedBy
      swapRequestedCarId
      swapStatus
      swapResponseDeadline
      swapStartTime
      swapNote
      car {
        id
        make
        model
        year
      }
    }
  }
`;

export const APPROVE_CAR_SWAP = gql`
  mutation ApproveCarSwap($input: CarSwapResponseInput!) {
    approveCarSwap(input: $input) {
      id
      carId
      originalCarId
      swapStatus
      swapApprovedAt
      swapCompletedAt
      car {
        id
        make
        model
        year
      }
    }
  }
`;

export const REJECT_CAR_SWAP = gql`
  mutation RejectCarSwap($input: CarSwapResponseInput!) {
    rejectCarSwap(input: $input) {
      id
      swapStatus
      swapRejectedAt
      swapRequestedCarId
      swapResponseDeadline
    }
  }
`;

export const FORCE_APPROVE_CAR_SWAP = gql`
  mutation ForceApproveCarSwap($input: CarSwapResponseInput!) {
    forceApproveCarSwap(input: $input) {
      id
      carId
      originalCarId
      swapStatus
      swapApprovedAt
      swapCompletedAt
      car {
        id
        make
        model
        year
      }
    }
  }
`;

export const FORCE_DECLINE_CAR_SWAP = gql`
  mutation ForceDeclineCarSwap($input: CarSwapResponseInput!) {
    forceDeclineCarSwap(input: $input) {
      id
      swapStatus
      swapRejectedAt
      swapRequestedCarId
      swapResponseDeadline
    }
  }
`;

/**
 * Spec 019 T067: the car trip's handover job and the services site to book one
 * on, for the host. A query of its own, read with errorPolicy "ignore", so an
 * API without these fields never breaks the trip page; `handoverServicesSite`
 * is null while no brand sells services, and the card then stays hidden.
 */
export const GET_BOOKING_HANDOVER = gql`
  query GetBookingHandover($id: String!) {
    getBookingAsHost(id: $id) {
      id
      handoverServicesSite
      handoverJob {
        bookingId
        workerName
        status
        jobStatus
        serviceDate
        windowStartAt
        windowEndAt
        timezone
        brandSite
      }
    }
  }
`;

/**
 * Spec 028: the unit and currency a trip was booked in. Queries of their own
 * with `errorPolicy: 'ignore'`, so an API without the fields reads as miles and
 * USD and never breaks the trip page.
 */
export const BOOKING_LOCALE_AS_GUEST = gql`
  query BookingLocaleAsGuest($id: String!) {
    getBookingAsGuest(id: $id) {
      id
      taxLabel
      receipt {
        distanceUnit
        currency
      }
    }
  }
`;

export const BOOKING_LOCALE_AS_HOST = gql`
  query BookingLocaleAsHost($id: String!) {
    getBookingAsHost(id: $id) {
      id
      taxLabel
      receipt {
        distanceUnit
        currency
      }
    }
  }
`;

/** Spec 028: the same for pages that read the trip through `booking(id)`. */
export const BOOKING_LOCALE = gql`
  query BookingLocale($id: String!) {
    booking(id: $id) {
      id
      taxLabel
      receipt {
        distanceUnit
        currency
      }
    }
  }
`;
